import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {formatMoney} from '@shopify/theme-currency';

import iconClose from '../../../assets/svg/icon-cross.svg';
import iconAfterpay from '../../../assets/svg/icon-afterpay-alt.svg';
import {CartEmptyContent} from '../CartEmptyContent';
// eslint-disable-next-line @shopify/strict-component-boundaries
import {klaviyoTrackAddToCart} from '../../../assets/scripts/components/klaviyo-tracking-events';

import CartItem from './cart-item';
import FreeShippingBar from './free-shipping-bar';
import CartUpsells from './cart-upsells';
import CartPromo from './cart-promo';
import TrustIndicators from './trust-indicators';
import GiftWithPurchase from './gift-with-purchase/GiftWithPurchase';
import {getGiftWithPurchase} from './gift-with-purchase';

/* eslint-disable babel/camelcase */
const emptyCart = {
  item_count: 0,
  items: [],
  total_price: 0,
};
/* eslint-enable babel/camelcase */

const SideCart = ({rootUrl, freeShippingBarSettings}) => {
  const isCartPage = window.location.pathname === '/cart' || window.location.pathname === '/cart/';
  const defaultCart = window.theme.cart ? window.theme.cart : emptyCart;

  const [cart, setCart] = useState(defaultCart);
  const [customCartData, setCustomCartData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const cartItems = cart.items;
  const total = cart.total_price;

  const afterpayEnabled = window.theme.cartAfterpayEnabled;
  const paymentDrawerEnabled = window.theme.cartPaymentDrawerEnabled;
  const trustIndicators = window.theme.trustIndicators || [];

  const giftWithPurchaseEnabled = window.theme.gwp?.enabled ?? false;
  const gifts = JSON.parse(document.querySelector('[data-gift-with-purchase]')?.innerHTML ?? '[]');
  const gwp = getGiftWithPurchase(gifts, cartItems);
  const [gwpManuallyRemoved, setGwpManuallyRemoved] = useState(
    () => sessionStorage.getItem('gwpManuallyRemoved') === 'true'
  );

  useEffect(() => {
    if (gwpManuallyRemoved) {
      sessionStorage.setItem('gwpManuallyRemoved', 'true');
    } else {
      sessionStorage.removeItem('gwpManuallyRemoved');
    }
  }, [gwpManuallyRemoved]);

  const promoEnabled = window.theme.cartPromo ? window.theme.cartPromo.enabled : false;

  const [upsellProducts, setUpsellProducts] = useState([]);
  const upsellsEnabled = window.theme.cartUpsells ? window.theme.cartUpsells.enabled : false;

  const getUpsellProducts = async (items) => {
    const _items = items ? items : cartItems;
    if (_items.length <= 0) {
      return;
    }

    // upsellsGlobalProducts is an array with product handles coming from theme settings for upsells
    let upsellsGlobalProducts =
      window.theme.cartUpsells && window.theme.cartUpsells.products ? window.theme.cartUpsells.products : [];

    if (upsellsGlobalProducts.length > 0) {
      // remove any handles if product already in the cart
      upsellsGlobalProducts = upsellsGlobalProducts.filter(
        (upsellHandle) => !_items.some((item) => item.handle === upsellHandle)
      );
    }

    const maxProducts = 3;
    const upsellsHandlesAll = _items.reduce((handles, item) => {
      if (item.properties && '_upsells' in item.properties) {
        let itemUpsellsHandles = item.properties._upsells.split('|');
        // remove any handles of products already in the cart
        itemUpsellsHandles = itemUpsellsHandles.filter((handle) => {
          const isInCart = _items.some((cartItem) => cartItem.handle === handle);
          return !isInCart;
        });
        return [...handles, ...itemUpsellsHandles];
      }
      return handles;
    }, upsellsGlobalProducts);

    // create a final handles array from theme settings and line item properties with no duplicates
    const upsellsHandles = [...new Set(upsellsHandlesAll)];

    let upsellsProducts = [];

    if (upsellsHandles.length > 0) {
      // get upsell products using ajax api...
      for (const handle of upsellsHandles) {
        // first check sessionStorage to see if we already have this product
        const upsellsProduct = sessionStorage.getItem(`__upsell:${handle}`);
        if (upsellsProduct) {
          upsellsProducts = [...upsellsProducts, JSON.parse(upsellsProduct)];
          continue;
        }
        // get the product data via the ajax api
        try {
          const url = `${rootUrl}/products/${handle}.js`;
          const options = {
            headers: {'Content-Type': 'application/json'},
          };
          const response = await fetch(url, options);
          if (response.ok === false) {
            throw new Error('Fetch response was not OK');
          }
          const json = await response.json();
          sessionStorage.setItem(`__upsell:${handle}`, JSON.stringify(json));
          upsellsProducts = [...upsellsProducts, json];
        } catch (error) {
          window.console.error(`Could not get product data for product with handle ${handle}`);
        }
      }
    }

    if (upsellsHandles.length < maxProducts) {
      // get upsell products using the product recommendations api...
      const mostExpensiveProduct = _items.reduce((prev, current) =>
        prev.final_price > current.final_price ? prev : current
      );
      const productHandle = mostExpensiveProduct.handle;
      const limit = maxProducts - upsellsHandles.length;
      // first check sessionStorage to see if we already have this product
      let upsellsRecommendations = sessionStorage.getItem(`__recommendations:${productHandle}`);
      if (upsellsRecommendations) {
        upsellsRecommendations = JSON.parse(upsellsRecommendations);
      } else {
        upsellsRecommendations = [];
        const productId = mostExpensiveProduct.product_id;
        const url = `${rootUrl}/recommendations/products.json?product_id=${productId}&limit=10`;
        const options = {
          headers: {'Content-Type': 'application/json'},
        };
        try {
          const response = await fetch(url, options);
          const json = await response.json();
          upsellsRecommendations = json.products;
          sessionStorage.setItem(`__recommendations:${productHandle}`, JSON.stringify(upsellsRecommendations));
        } catch (err) {
          window.console.error(err);
        }
      }
      // remove any products already in the cart or in the upsells section
      upsellsRecommendations = upsellsRecommendations.filter(({handle, tags}) => {
        const isPreoder = tags.some((tag) => tag === 'preorder');
        const isInCart = _items.some((item) => item.handle === handle);
        const isInUpsells = upsellsHandles.some((upsell) => upsell === handle);
        return !isInCart && !isInUpsells && !isPreoder;
      });
      upsellsProducts = [...upsellsProducts, ...upsellsRecommendations.slice(0, limit)];
    }

    setUpsellProducts(upsellsProducts);
  };

  const fetchCustomCartData = async () => {
    const url = `${rootUrl}/cart?view=custom-data`;
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('Fetch cart response was not OK');
      }
      const json = await response.json();
      return json;
    } catch (err) {
      window.console.error(err);
    }
  };

  const updateCart = (newCart) => {
    setCart(newCart);
    const counters = document.querySelectorAll('[data-items-count]');
    counters.forEach((counter) => (counter.innerHTML = newCart.item_count));
    if (upsellsEnabled) {
      getUpsellProducts(newCart.items);
    }
  };

  const fetchCart = async () => {
    const url = `${rootUrl}/cart.js`;
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('Fetch cart response was not OK');
      }
      const json = await response.json();
      return json;
    } catch (err) {
      window.console.error(err);
    }
  };

  /**
   *
   * @typedef {Object} Item
   * @property {number} id - The Shopify variant ID.
   * @property {number} quantity - The quantity to add of this variant.
   * @property {Object.<string, string>} [properties] - Optional line item properties.
   * @property {Object} [variant] - Optional variant.
   */

  /**
   *
   * @param {...Item} items - Array of {@link Item} objects to add to cart.
   */
  const addLineItems = async (items) => {
    const data = {
      items: [items].flat(),
    };
    const url = `${rootUrl}/cart/add.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        const errorResponse = await response.json();
        setErrorMessage(errorResponse.description);
      }
      const cartContents = await fetchCart();
      klaviyoTrackAddToCart(cartContents);
      updateCart(cartContents);

      // Fetch new custom cart data and update sessionStorage
      const newCustomCartData = await fetchCustomCartData();
      setCustomCartData(newCustomCartData);
      sessionStorage.setItem('cartCustomData', JSON.stringify(newCustomCartData));
    } catch (err) {
      window.console.error(err);
    }
  };

  const updateLineItems = async (updates, attributes, callback) => {
    const data = {};
    if (updates) {
      data.updates = updates;
    }
    if (attributes) {
      data.attributes = attributes;
    }
    const url = `${rootUrl}/cart/update.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('cart/update response was not OK');
      }
      const cartContents = await response.json();
      updateCart(cartContents);
      if (callback) {
        callback();
      }
    } catch (err) {
      window.console.error(err);
    }
  };

  const handleCartToggle = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (document.body.classList.contains('cart-is-visible')) {
      document.body.classList.remove('cart-is-visible');
      const cartOpenButton = document.querySelector('.header__btn--cart');
      if (cartOpenButton) {
        cartOpenButton.focus();
      }
    } else {
      document.body.classList.remove('menu-is-expanded');
      document.body.classList.add('cart-is-visible');
      const cartCloseButton = document.querySelector('.side-cart__close');
      if (cartCloseButton) {
        cartCloseButton.focus();
      }
    }
  };

  /**
   *
   * @param {...Item} items - Array of {@link Item} objects to add to cart.
   * @param {boolean} openCart - Triggers the cart to open when item added if true
   */

  const handleAddToCartEvent = async (items, openCart = false, callback) => {
    // Items could contain extra data like properties or variant info.
    // This reassignment strips out all but id, qty and properties to pass
    // to the addLineItems function for the cart.
    const lineItems = [items].flat().map((item) => {
      // ID and Qty will always be part of the array
      const newLineItem = {
        id: item.id,
        quantity: item.quantity,
      };
      // Only add properties if they exist
      if ('properties' in item) {
        newLineItem.properties = item.properties;
      }
      return newLineItem;
    });
    try {
      await addLineItems(lineItems);
      if (openCart) {
        handleCartToggle();
      }
      if (callback) {
        callback();
      }
    } catch (err) {
      window.console.error(err);
    }
  };

  // Initialize custom cart data from sessionStorage or fetch new custom cart data
  const initializeCustomCartData = async () => {
    const savedCartCustomData = sessionStorage.getItem('cartCustomData');
    if (savedCartCustomData && savedCartCustomData !== 'undefined') {
      setCustomCartData(JSON.parse(savedCartCustomData));
    } else {
      const newCustomCartData = await fetchCustomCartData();
      setCustomCartData(newCustomCartData);
      sessionStorage.setItem('cartCustomData', JSON.stringify(newCustomCartData));
    }
  };

  useEffect(() => {
    window.sideCart = {
      handleAddToCartEvent,
    };

    initializeCustomCartData();

    const cartToggles = document.querySelectorAll('[data-toggle-cart]');
    cartToggles.forEach((cartToggle) => cartToggle.addEventListener('click', handleCartToggle));
    if (upsellsEnabled) {
      getUpsellProducts();
    }
  }, []);

  const handlePaymentDrawerToggle = () => {
    const paymentDrawer = document.querySelector('.payment-drawer');
    if (!paymentDrawer) return;
    paymentDrawer.classList.toggle('drawer--active');
    document.body.classList.toggle('scroll-lock');
  };

  return (
    <>
      {isCartPage ? (
        <div className="page-header">
          <h1 className="side-cart__title text-center">{window.theme.strings.cartPageTitle}</h1>
        </div>
      ) : (
        <div className="side-cart__header">
          <h2 className="side-cart__title h7">{window.theme.strings.cartTitle}</h2>
          <button
            type="button"
            className="side-cart__close btn-none"
            aria-label={window.theme.strings.closeCart}
            data-toggle-cart
            dangerouslySetInnerHTML={{__html: iconClose}}
          />
        </div>
      )}

      {cartItems.length > 0 && freeShippingBarSettings.enabled && (
        <FreeShippingBar total={total} freeShippingBarSettings={freeShippingBarSettings} />
      )}

      {errorMessage && (
        <div className="side-cart__error-message">
          {errorMessage}
          <button
            type="button"
            className="side-cart__dismiss-message"
            aria-label={window.theme.strings.dismissErrorMessage}
            onClick={() => setErrorMessage(null)}
          >
            <span dangerouslySetInnerHTML={{__html: iconClose}} />
          </button>
        </div>
      )}

      {cartItems && cartItems.length ? (
        <div className="side-cart__content-container">
          <div className="side-cart__content">
            <ul className="side-cart__cart-items-container list-none">
              {cartItems.map((item) => {
                const customData = customCartData?.items?.find((customCartItem) => customCartItem.id === item.id) ?? {};

                return (
                  <CartItem
                    item={item}
                    customData={customData}
                    updateLineItems={updateLineItems}
                    key={item.key}
                    isCartPage={isCartPage}
                    setGwpManuallyRemoved={setGwpManuallyRemoved}
                  />
                );
              })}
            </ul>

            {!isCartPage && cartItems.length > 0 && giftWithPurchaseEnabled && (
              <GiftWithPurchase
                gifts={gwp.gifts}
                excludedGifts={gwp.excludedGifts}
                claimedGifts={gwp.claimedGifts}
                remainingSpend={gwp.remainingSpend}
                cartItems={cartItems}
                addToCart={handleAddToCartEvent}
                updateLineItems={updateLineItems}
                gwpManuallyRemoved={gwpManuallyRemoved}
                setGwpManuallyRemoved={setGwpManuallyRemoved}
              />
            )}

            {promoEnabled && <CartPromo />}

            {upsellsEnabled && !isCartPage && (
              <CartUpsells
                className={total === 0 ? 'hide' : ''}
                total={total}
                products={upsellProducts}
                addToCart={handleAddToCartEvent}
              />
            )}

            {trustIndicators.length > 0 && !isCartPage && <TrustIndicators indicators={trustIndicators} />}
          </div>

          <div className="side-cart__totals">
            {isCartPage && (
              <div className="side-cart__summary">
                <h2 className="side-cart__summary-title h4">Order Summary</h2>
                {trustIndicators.length > 0 && isCartPage && <TrustIndicators indicators={trustIndicators} />}
              </div>
            )}
            <div className="side-cart__totals-inner">
              <div className="side-cart__subtotals">
                <div className="side-cart__subtotal side-cart__totals-row">
                  <span className="h7">{window.theme.strings.subtotal}</span>
                  <span className="h7">{formatMoney(total, window.theme.moneyFormat)}</span>
                </div>
                <div className="side-cart__shipping side-cart__totals-row">
                  <span className="h7">{window.theme.strings.shipping}</span>
                  <span className="side-cart__subtotal-value h7">
                    {window.theme.strings.calculatedAtCheckout}
                  </span>
                </div>
                {isCartPage && (
                  <>
                    {freeShippingBarSettings &&
                      freeShippingBarSettings.enabled &&
                      freeShippingBarSettings.thresholdOne &&
                      freeShippingBarSettings.thresholdOne !== '' && (
                        <div className="side-cart__total side-cart__totals-row">
                          <span className="h7">{window.theme.strings.total}</span>
                          <span className="h7">
                            {total >= Number(freeShippingBarSettings.thresholdOne)
                              ? formatMoney(total, window.theme.moneyFormat)
                              : formatMoney(window.theme.shippingPrice * 100 + total, window.theme.moneyFormat)}
                          </span>
                        </div>
                      )}

                    {afterpayEnabled && (
                      <div class="cart__afterpay">
                        <p class="text-small">
                          {window.theme.strings.afterpay.replace(
                            '{{ instalment }}',
                            formatMoney(
                              total >= Number(freeShippingBarSettings.thresholdOne)
                                ? total / 4
                                : (window.theme.shippingPrice * 100 + total) / 4,
                              window.theme.moneyFormat
                            )
                          )}
                          <span dangerouslySetInnerHTML={{__html: iconAfterpay}} />
                          {paymentDrawerEnabled && (
                            <button class="btn-none text-small" type="button" onClick={handlePaymentDrawerToggle}>
                              {window.theme.strings.afterpayLink}
                            </button>
                          )}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="side-cart__checkout-wrapper">
                <div className="side-cart__checkout">
                  <a href={`${rootUrl}/checkout`} className="btn btn-checkout btn--primary btn--block">
                    {window.theme.strings.checkout}
                  </a>
                </div>
                <div className="side-cart__checkout side-cart__checkout--continue">
                  {isCartPage ? (
                    <a href="/collections" className="btn btn--secondary btn--block">
                      {window.theme.strings.continueShopping}
                    </a>
                  ) : (
                    <button type="button" className="btn btn--secondary btn--block" data-toggle-cart>
                      {window.theme.strings.continueShopping}
                    </button>
                  )}
                </div>
              </div>
              {!isCartPage && (
                <div className="side-cart__totals-bottom">
                  <a href="/cart" className="cart-link">
                    {window.theme.strings.viewBag}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <CartEmptyContent />
      )}
    </>
  );
};

SideCart.propTypes = {
  rootUrl: PropTypes.string,
  freeShippingBarSettings: PropTypes.func,
};

export default SideCart;
